import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/shared/Logo";
import CtaButton from "@/components/shared/CtaButton";
import { NAV } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-gray-100" : "bg-white/0 border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
                className="relative px-4 py-2 text-sm font-medium text-gray-600 hover:text-[#111111] transition-colors"
              >
                {item.label}
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-[#F36F21] rounded-full"
                  />
                )}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:block">
          <CtaButton variant="primary" source="navbar" title="Request AI Visibility Audit" testId="nav-cta" arrow={false} className="px-5 py-2.5 text-sm">
            Request Audit
          </CtaButton>
        </div>

        <button
          className="lg:hidden text-[#111111]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-gray-100 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="px-2 py-3 text-base font-medium text-[#111111] border-b border-gray-50"
                  data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-4">
                <CtaButton variant="accent" source="mobile-nav" title="Request AI Visibility Audit" testId="mobile-nav-cta" className="w-full">
                  Request AI Visibility Audit
                </CtaButton>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
